import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { Plus, Search, Mail, Phone, MapPin, Edit, Eye, Filter, Grid3x3, List, Send, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('name_asc');
  const [filterStatus, setFilterStatus] = useState('active'); // Default to active only
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [formData, setFormData] = useState({
    user_id: '',
    full_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    join_date: new Date().toISOString().split('T')[0],
    employment_type: 'full_time',
    salary_basic: 0,
    salary_allowances: 0,
    ic_number: '',
    nationality: 'malaysian',
    marital_status: 'single',
    spouse_working: false,
    number_of_children: 0,
    disabled_self: false,
    disabled_spouse: false,
    disabled_children: 0,
    epf_number: '',
    socso_number: '',
    bank_name: '',
    bank_account: ''
  });
  const [sendInvitation, setSendInvitation] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');
  const canManage = ['admin', 'hr_manager', 'super_admin', 'platform_admin'].includes(user.role);

  const handleResendInvitation = async (e, employeeId) => {
    e.stopPropagation();
    setResendingId(employeeId);
    try {
      const token = localStorage.getItem('hrToken') || localStorage.getItem('authToken');
      await axios.post(`${API}/employees/${employeeId}/resend-invitation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Activation email sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend activation email');
    } finally {
      setResendingId(null);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let filtered = employees.filter(emp => 
      emp.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.employee_id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(emp => emp.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'name_asc':
          return a.full_name.localeCompare(b.full_name);
        case 'name_desc':
          return b.full_name.localeCompare(a.full_name);
        case 'department_asc':
          return (a.department || '').localeCompare(b.department || '');
        case 'department_desc':
          return (b.department || '').localeCompare(a.department || '');
        case 'join_date_newest':
          return new Date(b.join_date) - new Date(a.join_date);
        case 'join_date_oldest':
          return new Date(a.join_date) - new Date(b.join_date);
        default:
          return 0;
      }
    });

    setFilteredEmployees(filtered);
  }, [searchQuery, employees, sortOrder, filterStatus]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('hrToken') || localStorage.getItem('authToken');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
      setFilteredEmployees(response.data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      full_name: '',
      email: '',
      phone: '',
      department: '',
      position: '',
      join_date: new Date().toISOString().split('T')[0],
      employment_type: 'full_time',
      salary_basic: 0,
      salary_allowances: 0,
      ic_number: '',
      nationality: 'malaysian',
      marital_status: 'single',
      spouse_working: false,
      number_of_children: 0,
      disabled_self: false,
      disabled_spouse: false,
      disabled_children: 0,
      epf_number: '',
      socso_number: '',
      bank_name: '',
      bank_account: ''
    });
    setSendInvitation(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        salary_basic: parseFloat(formData.salary_basic) || 0,
        salary_allowances: parseFloat(formData.salary_allowances) || 0,
        send_invitation: sendInvitation
      };
      
      // Only add new employee (edit is handled by separate page)
      await axios.post(`${API}/employees`, payload);
      toast.success('Employee added successfully!');
      
      setDialogOpen(false);
      fetchEmployees();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Employees</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">{employees.length} total employees</p>
        </div>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-slate-900 hover:bg-slate-800" 
                data-testid="add-employee-btn"
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl h-[85vh] p-0 flex flex-col">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
                <DialogTitle className="text-xl font-bold">Add New Employee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" data-testid="add-employee-form">
                <div className="overflow-y-scroll flex-1 px-6 py-6 space-y-6" style={{maxHeight: 'calc(85vh - 140px)'}}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+60123456789"
                    />
                  </div>
                  <div>
                    <Label>IC Number</Label>
                    <Input
                      value={formData.ic_number}
                      onChange={(e) => setFormData({ ...formData, ic_number: e.target.value })}
                      placeholder="990101-01-1234"
                    />
                  </div>
                  <div>
                    <Label>Nationality</Label>
                    <select
                      value={formData.nationality}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-300 px-3"
                    >
                      <option value="malaysian">Malaysian</option>
                      <option value="non_malaysian_permanent">Non-Malaysian (Permanent Resident)</option>
                      <option value="non_malaysian_temporary">Non-Malaysian (Temporary)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      placeholder="Engineering"
                    />
                  </div>
                  <div>
                    <Label>Position</Label>
                    <Input
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      placeholder="Senior Developer"
                    />
                  </div>
                  <div>
                    <Label>Join Date</Label>
                    <Input
                      required
                      type="date"
                      value={formData.join_date}
                      onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Employment Type</Label>
                    <select
                      value={formData.employment_type}
                      onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-300 px-3"
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <Label>Basic Salary (RM)</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      value={formData.salary_basic}
                      onChange={(e) => setFormData({ ...formData, salary_basic: e.target.value })}
                      placeholder="5000.00"
                    />
                  </div>
                  <div>
                    <Label>Allowances (RM)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.salary_allowances}
                      onChange={(e) => setFormData({ ...formData, salary_allowances: e.target.value })}
                      placeholder="500.00"
                    />
                  </div>
                </div>

                {/* Tax Relief Information */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Tax Relief Information (for PCB Calculation)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Marital Status</Label>
                      <select
                        value={formData.marital_status}
                        onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                        className="w-full h-10 rounded-md border border-slate-300 px-3"
                      >
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                      </select>
                    </div>
                    {formData.marital_status === 'married' && (
                      <>
                        <div className="flex items-center space-x-2 pt-6">
                          <input
                            type="checkbox"
                            id="spouse_working"
                            checked={formData.spouse_working}
                            onChange={(e) => setFormData({ ...formData, spouse_working: e.target.checked })}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                          <Label htmlFor="spouse_working" className="mb-0">Spouse Working</Label>
                        </div>
                      </>
                    )}
                    <div>
                      <Label>Number of Children</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.number_of_children}
                        onChange={(e) => setFormData({ ...formData, number_of_children: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>Disabled Children</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.disabled_children}
                        onChange={(e) => setFormData({ ...formData, disabled_children: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="flex items-center space-x-2 pt-6">
                      <input
                        type="checkbox"
                        id="disabled_self"
                        checked={formData.disabled_self}
                        onChange={(e) => setFormData({ ...formData, disabled_self: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <Label htmlFor="disabled_self" className="mb-0">Employee is Disabled</Label>
                    </div>
                    {formData.marital_status === 'married' && (
                      <div className="flex items-center space-x-2 pt-6">
                        <input
                          type="checkbox"
                          id="disabled_spouse"
                          checked={formData.disabled_spouse}
                          onChange={(e) => setFormData({ ...formData, disabled_spouse: e.target.checked })}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        <Label htmlFor="disabled_spouse" className="mb-0">Spouse is Disabled</Label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Statutory Information */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Statutory Numbers</h4>
                  <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>EPF Number</Label>
                    <Input
                      value={formData.epf_number}
                      onChange={(e) => setFormData({ ...formData, epf_number: e.target.value })}
                      placeholder="EPF123456"
                    />
                  </div>
                  <div>
                    <Label>SOCSO Number</Label>
                    <Input
                      value={formData.socso_number}
                      onChange={(e) => setFormData({ ...formData, socso_number: e.target.value })}
                      placeholder="SOCSO123456"
                    />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="Maybank"
                    />
                  </div>
                  <div>
                    <Label>Bank Account</Label>
                    <Input
                      value={formData.bank_account}
                      onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                      placeholder="123456789012"
                    />
                  </div>
                </div>
                </div>

                {/* Send Invitation Option */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700/50 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="sendInvitation"
                        checked={sendInvitation}
                        onChange={(e) => setSendInvitation(e.target.checked)}
                        data-testid="send-invitation-checkbox"
                        className="h-5 w-5 rounded border-slate-300 dark:border-slate-600 text-emerald-600 mt-0.5 bg-white dark:bg-slate-800"
                      />
                      <div className="flex-1">
                        <Label htmlFor="sendInvitation" className="text-slate-900 dark:text-slate-100 font-semibold cursor-pointer">
                          Send Invitation Email to Employee
                        </Label>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          Employee will receive an email with an activation link to set their password and access the system. 
                          The invitation link will be valid for 7 days.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                </div>
                <div className="flex justify-end space-x-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 shrink-0">
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700">
                    Add Employee
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            data-testid="search-employees-input"
            placeholder="Search employees by name, email, department..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11"
          />
        </div>
        
        {/* Sort Dropdown */}
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="h-11 px-4 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
        >
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
          <option value="department_asc">Department (A-Z)</option>
          <option value="department_desc">Department (Z-A)</option>
          <option value="join_date_newest">Newest First</option>
          <option value="join_date_oldest">Oldest First</option>
        </select>

        {/* Status Filter Dropdown */}
        <div className="relative">
          <Button 
            variant="outline" 
            className="h-11"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {filterStatus === 'all' ? 'All Status' : filterStatus === 'inactive' ? 'Pending Activation' : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
          </Button>
          
          {showFilterDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-50">
              <button
                onClick={() => { setFilterStatus('all'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'all' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                All Status
              </button>
              <button
                onClick={() => { setFilterStatus('active'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'active' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                Active
              </button>
              <button
                onClick={() => { setFilterStatus('inactive'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'inactive' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                Pending Activation
              </button>
              <button
                onClick={() => { setFilterStatus('terminated'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'terminated' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                Terminated
              </button>
            </div>
          )}
        </div>

        {/* View Toggle */}
        <div className="flex border border-slate-300 dark:border-slate-600 rounded-md overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2.5 transition-colors ${
              viewMode === 'grid'
                ? 'bg-slate-900 dark:bg-slate-700 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
            title="Grid View"
          >
            <Grid3x3 className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2.5 transition-colors ${
              viewMode === 'list'
                ? 'bg-slate-900 dark:bg-slate-700 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
            title="List View"
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Employees Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEmployees.map((employee) => (
            <div
              key={employee.employee_id}
              data-testid={`employee-card-${employee.employee_id}`}
              onClick={() => navigate(`/suite/hr/employees/${employee.employee_id}`)}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm card-hover cursor-pointer transition-all hover:shadow-md hover:border-slate-300 dark:hover:border-slate-500"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">
                      {employee.full_name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">{employee.full_name}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{employee.employee_id}</p>
                  </div>
                </div>
                <span className={`status-badge ${
                  employee.status === 'active' ? 'status-active' : 'status-inactive'
                }`}>
                  {employee.status === 'inactive' ? 'Pending Activation' : employee.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                  <MapPin className="h-4 w-4" />
                  <span>{employee.department || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                    <Phone className="h-4 w-4" />
                    <span>{employee.phone}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 dark:border-slate-700 pt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Position</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{employee.position || 'N/A'}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {canManage && employee.status === 'inactive' && (
                    <button
                      onClick={(e) => handleResendInvitation(e, employee.employee_id)}
                      disabled={resendingId === employee.employee_id}
                      className="flex items-center text-sm text-amber-600 hover:text-amber-700 disabled:opacity-50"
                      title="Resend activation email"
                    >
                      {resendingId === employee.employee_id ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-1" />
                      )}
                      Resend
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/suite/hr/employees/${employee.employee_id}/edit`);
                      }}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  )}
                  <div className="flex items-center text-sm text-emerald-600">
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Employees List View */}
      {viewMode === 'list' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Employee</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Department</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Position</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Contact</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {filteredEmployees.map((employee) => (
                <tr 
                  key={employee.employee_id} 
                  className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                  onClick={() => navigate(`/suite/hr/employees/${employee.employee_id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {employee.full_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{employee.full_name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{employee.employee_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900 dark:text-slate-100">{employee.department || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900 dark:text-slate-100">{employee.position || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <p className="text-sm text-slate-600 dark:text-slate-400">{employee.email}</p>
                      {employee.phone && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">{employee.phone}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`status-badge ${
                      employee.status === 'active' ? 'status-active' : 'status-inactive'
                    }`}>
                      {employee.status === 'inactive' ? 'Pending Activation' : employee.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {canManage && employee.status === 'inactive' && (
                        <button 
                          onClick={(e) => handleResendInvitation(e, employee.employee_id)}
                          disabled={resendingId === employee.employee_id}
                          className="flex items-center text-sm text-amber-600 hover:text-amber-700 disabled:opacity-50"
                          title="Resend activation email"
                        >
                          {resendingId === employee.employee_id ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-1" />
                          )}
                          Resend
                        </button>
                      )}
                      {canManage && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/suite/hr/employees/${employee.employee_id}/edit`);
                          }}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      )}
                      <button className="flex items-center text-sm text-emerald-600 hover:text-emerald-700">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredEmployees.length === 0 && !loading && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">No employees found</p>
        </div>
      )}
    </div>
  );
}